import { useState } from 'react'

import { EMPTY_OBJECT, EMPTY_STRING } from '../../../../../constants/common'
import { useDebounceFunc } from '../../../../../hooks'
import { isValueStringType } from '../../../../../utils/common'
import { useGoogleMapApiPlacesAutocomplete } from '../../../../Google'

import { ADDRESS_SELECT_GOOGLE_MAPS_API_PLACES_AUTOCOMPLETE_DEBOUNCE_TIME } from './useAddressSelectGoogleMapsApiPlacesAutocomplete.constants'
import { toAddressSelectGoogleMapsPlaceItems } from './useAddressSelectGoogleMapsApiPlacesAutocomplete.utils'

export function useAddressSelectGoogleMapsApiPlacesAutocomplete({
  debounceTime = ADDRESS_SELECT_GOOGLE_MAPS_API_PLACES_AUTOCOMPLETE_DEBOUNCE_TIME,
  maxLength,
  value,
  onChangeSearch,
} = EMPTY_OBJECT) {
  // Search text derived from the external `value`: the selected option's label
  // (option from dropdown) or the raw string (external text update).
  const valueSearchText = value?.value
    ? value.label
    : isValueStringType(value)
      ? value
      : null

  const [searchValue, setSearchValue] = useState(
    valueSearchText ?? EMPTY_STRING
  )
  const [prevValue, setPrevValue] = useState(value)

  // Sync the search field when `value` changes externally — during render
  // (initial value is set above, so this only handles subsequent changes).
  if (value !== prevValue) {
    setPrevValue(value)

    if (valueSearchText !== null) {
      setSearchValue(valueSearchText)
    }
  }

  const {
    clearSuggestions,
    fetchPredictions,
    handleResetSession,
    isInitiated,
    isLoading,
    suggestions,
  } = useGoogleMapApiPlacesAutocomplete({
    toPreparedItems: toAddressSelectGoogleMapsPlaceItems,
  })

  const {
    cancelDebounce: cancelDebouncedPredictionsFetch,
    debouncedHandler: debouncedFetchPredictions,
  } = useDebounceFunc(fetchPredictions, debounceTime)

  function handleChangeSelectSearch(nextValue) {
    if (nextValue.length <= maxLength) {
      cancelDebouncedPredictionsFetch()

      onChangeSearch(nextValue)
      setSearchValue(nextValue)

      debouncedFetchPredictions(nextValue)
    }
  }

  function handleClearSelectSearch() {
    cancelDebouncedPredictionsFetch()
    clearSuggestions()

    onChangeSearch(null)
    setSearchValue(EMPTY_STRING)
  }

  return {
    fetchPredictions,
    handleChangeSelectSearch,
    handleClearSelectSearch,
    handleResetSession,
    isInitiated,
    isLoading,
    searchValue,
    suggestions,
  }
}
